import React, { useEffect, useState } from 'react';
import { Box, Card, CardActionArea, CardContent, CircularProgress, Grid, Pagination, Stack, Typography, Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Chip, Alert } from '@mui/material';
import { fetchAllNews } from '../../controllers/newsController';
import { resolveNewsAssetUrl } from '../../api/httpClientNews';
import { resolveGalleryAssetUrl } from '../../api/httpClientGalleries';
import CreateNewsDialog from './CreateNewsDialog';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { deleteNews as deleteNewsAction, fetchNewsById } from '../../controllers/newsController';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import Skeleton from 'react-loading-skeleton';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditNewsDialog from '../../components/EditNewsDialog';

const resolveNewsCoverUrl = (path) => {
  if (!path) return '';
  if (path.includes('AllGalleries') || path.startsWith('/api/galleries')) {
    return resolveGalleryAssetUrl(path);
  }
  return resolveNewsAssetUrl(path);
};

const stripHtml = (value) => {
    if (typeof value !== 'string') return '';
    return value.replace(/<[^>]*>/g, '').trim();
};

const NewsList = () => {
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [createSuccessMessage, setCreateSuccessMessage] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [selectedNewsId, setSelectedNewsId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        let active = true;
        (async () => {
            setLoading(true);
            setError('');
            try {
                const { data, meta } = await fetchAllNews({ pageNumber: page, pageSize });
                if (!active) return;
                setItems(data || []);
                setTotalPages(meta?.totalPages || 1);
            } catch (e) {
                if (!active) return;
                setError(e?.message || 'Haberler alınamadı');
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [page, pageSize]);

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Haberler</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon sx={{ width: 16 }} />}
                    sx={{
                        textTransform: 'none',
                        backgroundColor: '#E7EDF4',
                        boxShadow: 'none',
                        fontSize: 13,
                        borderRadius: '5px',
                        color: '#0E4D94',
                        '&:hover': {
                            backgroundColor: '#CFDBEA',
                            boxShadow: 'none',
                        },
                    }}
                    onClick={() => setCreateOpen(true)}
                >
                    Yeni Haber
                </Button>
            </Stack>
            {createSuccessMessage && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreateSuccessMessage('')}>
                    {createSuccessMessage}
                </Alert>
            )}
            {error ? (
                <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                        gap: 2,
                    }}
                >
                    {loading ? (
                        Array.from({ length: pageSize }).map((_, index) => (
                            <Card key={`skeleton-${index}`} sx={{ width: '100%', position: 'relative', backgroundColor: '#f2f2f2', border: 'none', boxShadow: 'none', borderRadius: '8px', overflow: 'hidden' }}>
                                <Skeleton width="100%" height={200} />
                                <CardContent sx={{ p: 2, backgroundColor: '#f2f2f2' }}>
                                    <Stack spacing={1.5}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Skeleton width={80} height={12} />
                                            <Skeleton width={100} height={12} />
                                        </Box>
                                        <Skeleton width="95%" height={20} />
                                        <Skeleton width="100%" height={16} count={3} />
                                        <Skeleton width={100} height={16} />
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))
                    ) : items.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, gridColumn: '1 / -1' }}>
                            <Typography sx={{ color: '#a3a3a3' }}>Henüz haber eklenmemiş</Typography>
                        </Box>
                    ) : (
                        items.map((n) => (
                            <Card
                                sx={{
                                    width: '100%',
                                    position: 'relative',
                                    backgroundColor: '#f2f2f2',
                                    border: 'none',
                                    boxShadow: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    overflow: 'hidden',
                                    '&:hover': {
                                        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                                    },
                                }}
                            >
                                <CardActionArea onClick={() => navigate(`/news/${n.id}`)} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                                    {/* Kategori Tag */}
                                    {n.category && (
                                        <Box sx={{ position: 'absolute', top: 12, left: 12, zIndex: 1 }}>
                                            <Chip
                                                label={n.category}
                                                size="small"
                                                sx={{
                                                    backgroundColor: '#17C653',
                                                    color: '#ffffff',
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    height: '22px',
                                                    borderRadius: '12px',
                                                    '& .MuiChip-label': {
                                                        px: 1.5,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    )}
                                    {/* Görsel */}
                                    {n.coverImageUrl && (
                                        <Box sx={{ width: '100%', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#ffffff', overflow: 'hidden', position: 'relative' }}>
                                            <Box
                                                component="img"
                                                src={resolveNewsCoverUrl(n.coverImageUrl)}
                                                alt={n.title}
                                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        </Box>
                                    )}
                                    {/* İçerik */}
                                    <CardContent sx={{ p: 3, backgroundColor: '#f2f2f2', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 200 }}>
                                        <Stack spacing={1.5} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                            {/* Yazar ve Tarih */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="caption" sx={{ fontSize: '11px', color: '#666666' }}>
                                                    {new Date(n.createdAtUtc).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                                </Typography>
                                            </Box>
                                            {/* Başlık */}
                                            <Typography sx={{ fontWeight: 500, fontSize: 16, color: '#000000', marginTop: '8px!important' }}>
                                                {n.title}
                                            </Typography>
                                            {/* Özet */}
                                            <Typography variant="body2" sx={{ fontSize: 13, color: '#666666', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginTop: '5px!important', flex: 1 }}>
                                                {stripHtml(n.summary)}
                                            </Typography>
                                            {/* Read Article Link */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 'auto' }}>
                                                <Typography variant="body2" sx={{ fontSize: 13, color: '#0E4D94', fontWeight: 600, borderBottom: '1px solid #0E4D94' }}>
                                                    Haberi görüntüle
                                                </Typography>
                                                <ArrowForwardIcon sx={{ fontSize: 14, color: '#0E4D94' }} />
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                                <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
                                    <Tooltip title="Düzenle">
                                        <IconButton
                                            size="small"
                                            aria-label="Düzenle"
                                            sx={{
                                                backgroundColor: '#E7EDF4',
                                                borderRadius: '6px',
                                                width: 32,
                                                height: 32,
                                                '&:hover': {
                                                    backgroundColor: '#CFDBEA',
                                                },
                                                '& svg': {
                                                    fontSize: '16px',
                                                    color: '#0E4D94',
                                                },
                                            }}
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                try {
                                                    const newsData = await fetchNewsById(n.id);
                                                    setSelectedNewsId(newsData.id);
                                                    setEditOpen(true);
                                                } catch (err) {
                                                    alert(err?.message || 'Haber bilgileri alınamadı');
                                                }
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Sil">
                                        <IconButton
                                            size="small"
                                            aria-label="Sil"
                                            sx={{
                                                backgroundColor: '#FFEEF3',
                                                borderRadius: '6px',
                                                width: 32,
                                                height: 32,
                                                '&:hover': {
                                                    backgroundColor: '#FFD6E0',
                                                },
                                                '& svg': {
                                                    fontSize: '16px',
                                                    color: '#F8285A',
                                                },
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelected(n);
                                                setConfirmOpen(true);
                                            }}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Card>
                        ))
                    )}
                </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} />
            </Box>
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        fontSize: '18px',
                        fontWeight: 500,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pr: 3,
                    }}
                >
                    Haberi Sil
                    <IconButton
                        onClick={() => setConfirmOpen(false)}
                        size="small"
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ py: 2, px: 3 }}>
                    <DialogContentText sx={{ fontSize: '14px', color: '#666666' }}>
                        {selected ? `"${selected.title}" haberini silmek istediğinize emin misiniz?` : 'Seçili haberi silmek istediğinize emin misiniz?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ py: 2, px: 3 }}>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            color: '#666666',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        İptal
                    </Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            if (!selected) return;
                            try {
                                await deleteNewsAction(selected.id);
                                setConfirmOpen(false);
                                setSelected(null);
                                const { data, meta } = await fetchAllNews({ pageNumber: page, pageSize });
                                setItems(data || []);
                                setTotalPages(meta?.totalPages || 1);
                            } catch (e) { }
                        }}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            backgroundColor: '#F8285A',
                            borderRadius: '5px',
                            px: 2,
                            '&:hover': {
                                backgroundColor: '#d91e47',
                            },
                        }}
                    >
                        Sil
                    </Button>
                </DialogActions>
            </Dialog>
            <CreateNewsDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={async ({ sendPushNotification } = {}) => {
                    setCreateOpen(false);
                    if (sendPushNotification) {
                        setCreateSuccessMessage('Haber oluşturuldu ve üyelere bildirim gönderildi.');
                    } else {
                        setCreateSuccessMessage('');
                    }
                    const { data, meta } = await fetchAllNews({ pageNumber: page, pageSize });
                    setItems(data || []);
                    setTotalPages(meta?.totalPages || 1);
                }}
            />
            <EditNewsDialog
                open={editOpen}
                onClose={() => {
                    setEditOpen(false);
                    setSelectedNewsId(null);
                }}
                newsId={selectedNewsId}
                onSaved={async () => {
                    setEditOpen(false);
                    setSelectedNewsId(null);
                    const { data, meta } = await fetchAllNews({ pageNumber: page, pageSize });
                    setItems(data || []);
                    setTotalPages(meta?.totalPages || 1);
                }}
            />
        </Box>
    );
};

export default NewsList;


